import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Monitor, Minimize2, SlidersHorizontal, RefreshCw, AlertTriangle, Loader2,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useAvisos } from '../lib/contextoAvisos';
import Modal from '../ui/Modal';
import { useCapa } from '../ui/navegacionReact';
import TiempoRelativo from '../ui/TiempoRelativo';

// El tablero del almacén: cómo va todo, de un vistazo.
//
// SE LEE DESDE DIEZ METROS. Ésa es toda la especificación de esta
// pantalla. Nadie la abre, nadie la filtra, nadie la interpreta: está
// colgada en la nave y quien pasa la mira medio segundo. Si hace falta
// acercarse a leerla, no sirve.
//
// LO QUE NUNCA PUEDE HACER: enseñar números viejos como si fueran de
// ahora. Un tablero congelado que parece vivo es peor que uno apagado —
// convence a quien pasa de que no hay nada que atender. Por eso, cuando
// deja de poder actualizarse, LO DICE Y DICE DESDE CUÁNDO, y los números
// se apagan.

// Cada cinco segundos. Es lo que tarda alguien en cruzar la nave, y es lo
// que hace que un botón de ayuda se vea antes de que la persona se canse
// de esperar.
const REFRESCO_MS = 5000;

// A partir de aquí los números dejan de ser de fiar y se dice. Son tres
// intentos fallidos: uno se pierde por cualquier cosa, tres es que algo
// pasa.
const RANCIO_MS = 16000;

const COLOR = {
  verde: {
    caja: 'bg-green-50 border-green-300', texto: 'text-green-900',
    banda: 'bg-green-600', etiqueta: 'Todo en orden',
  },
  amarillo: {
    caja: 'bg-amber-50 border-amber-300', texto: 'text-amber-900',
    banda: 'bg-amber-500', etiqueta: 'Atención',
  },
  rojo: {
    caja: 'bg-red-50 border-red-300', texto: 'text-red-900',
    banda: 'bg-red-600', etiqueta: 'Hay que ir',
  },
  gris: {
    caja: 'bg-gray-100 border-gray-300', texto: 'text-gray-700',
    banda: 'bg-gray-500', etiqueta: 'No se pudo saber',
  },
};

export default function Andon({ irA }) {
  const [datos, setDatos] = useState(null);
  const [leidoEn, setLeidoEn] = useState(null);
  const [primerError, setPrimerError] = useState(null);
  const [tv, setTv] = useState(false);
  useCapa(tv, () => setTv(false));                 // el modo cartel tapa todo: atrás lo quita
  const [ajustes, setAjustes] = useState(false);
  const [ahora, setAhora] = useState(() => Date.now());
  const { tieneNivel } = useAuth();

  const cargar = useCallback(
    () => api.get('/andon')
      .then((d) => { setDatos(d); setLeidoEn(Date.now()); setPrimerError(null); })
      .catch((e) => {
        // NO SE BORRA LO QUE HABÍA. Vaciar la pantalla ante un fallo de red
        // deja a quien pasa sin nada; lo correcto es seguir enseñándolo y
        // decir que ya no es de ahora.
        setPrimerError((v) => v ?? {
          cuando: Date.now(),
          mensaje: e instanceof ErrorApi ? e.message : 'Se perdió la conexión.',
        });
      }),
    [],
  );

  useEffect(() => {
    let vivo = true;
    cargar();
    const t = setInterval(() => { if (vivo) cargar(); }, REFRESCO_MS);
    // El reloj propio: sin esto, «hace 2 minutos» se quedaría escrito en
    // «hace 2 minutos» para siempre en cuanto el servidor dejara de
    // contestar, que es exactamente cuando esa frase importa.
    const reloj = setInterval(() => { if (vivo) setAhora(Date.now()); }, 1000);
    return () => { vivo = false; clearInterval(t); clearInterval(reloj); };
  }, [cargar]);

  const rancio = leidoEn !== null && ahora - leidoEn > RANCIO_MS;

  if (datos === null && primerError === null) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        Mirando cómo va el almacén…
      </div>
    );
  }

  if (datos === null) {
    return (
      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-900">
        <div className="font-semibold">No se pudo leer el estado del almacén.</div>
        <p className="text-sm mt-1">{primerError.mensaje}</p>
        <button
          type="button"
          onClick={cargar}
          className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                     font-semibold border border-red-300 bg-white hover:bg-red-50"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Volver a intentar
        </button>
      </div>
    );
  }

  return (
    <div className={tv
      ? 'fixed inset-0 z-50 bg-white overflow-y-auto p-6 space-y-5'
      : 'space-y-4'}
    >
      <Encabezado
        tv={tv} setTv={setTv}
        puedeAjustar={tieneNivel('ENCARGADO')}
        onAjustar={() => setAjustes(true)}
      />
      <Banda resumen={datos.resumen} rancio={rancio} tv={tv} />
      <Frescura leidoEn={leidoEn} rancio={rancio} error={primerError} ahora={ahora} />

      <div className={`grid gap-3 ${tv
        ? 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
        : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}
      >
        {(datos.cuadros || []).map((c) => (
          <Cuadro key={c.clave} c={c} tv={tv} rancio={rancio}
                  onIr={tv ? null : () => irA?.(c.modulo, {})} />
        ))}
      </div>

      {datos.eventos.length > 0 && <Eventos eventos={datos.eventos} tv={tv} />}

      {ajustes && (
        <ModalAjustes onCerrar={() => setAjustes(false)}
                      onGuardado={() => { setAjustes(false); cargar(); }} />
      )}
    </div>
  );
}

// ------------------------------------------------------------ encabezado
function Encabezado({ tv, setTv, puedeAjustar, onAjustar }) {
  const bloqueo = useRef(null);

  async function entrar() {
    setTv(true);
    try {
      await document.documentElement.requestFullscreen?.();
    } catch { /* el navegador puede no dejar: el modo grande funciona igual */ }
    try {
      // Que la pantalla de la nave no se apague sola a los cinco minutos.
      // Si el navegador no lo soporta, no pasa nada: se ve igual.
      bloqueo.current = await navigator.wakeLock?.request('screen');
    } catch { /* sin bloqueo de pantalla; el tablero sigue funcionando */ }
  }

  async function salir() {
    setTv(false);
    try { await document.exitFullscreen?.(); } catch { /* ya estaba fuera */ }
    try { await bloqueo.current?.release(); } catch { /* nada que soltar */ }
    bloqueo.current = null;
  }

  // Salir con Escape aunque el navegador haya soltado la pantalla completa
  // por su cuenta: si no, el tablero se quedaría tapando la aplicación sin
  // forma evidente de volver.
  useEffect(() => {
    if (!tv) return undefined;
    const fuera = () => { if (!document.fullscreenElement) setTv(false); };
    document.addEventListener('fullscreenchange', fuera);
    return () => document.removeEventListener('fullscreenchange', fuera);
  }, [tv, setTv]);

  if (tv) {
    return (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={salir}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        >
          <Minimize2 className="h-4 w-4" aria-hidden="true" />
          Salir del modo pantalla
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Cómo va el almacén</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          El estado de todo, ahora mismo. Se actualiza solo; cuélgalo en una
          pantalla y no hay que tocarlo.
        </p>
      </div>
      <div className="flex items-center gap-2">
        {puedeAjustar && (
          <button
            type="button"
            onClick={onAjustar}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            Ajustar colores
          </button>
        )}
        <button
          type="button"
          onClick={entrar}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-[var(--color-marca)] text-white hover:opacity-90"
        >
          <Monitor className="h-4 w-4" aria-hidden="true" />
          Modo pantalla
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- banda
export function Banda({ resumen, rancio, tv }) {
  // RANCIO MANDA SOBRE TODO. Un «TODO EN ORDEN» en verde gigante calculado
  // con datos de hace diez minutos es la peor pantalla posible.
  const c = COLOR[rancio ? 'gris' : resumen.estado] || COLOR.gris;
  return (
    <div className={`rounded-2xl ${c.banda} text-white px-6 ${tv ? 'py-10' : 'py-6'}`}>
      <div className={`font-bold leading-none ${tv ? 'text-6xl' : 'text-3xl'}`}>
        {rancio ? 'SIN DATOS DE AHORA' : resumen.frase}
      </div>
      {!rancio && (resumen.rojos > 0 || resumen.amarillos > 0) && (
        <div className={`mt-2 opacity-90 ${tv ? 'text-2xl' : 'text-sm'}`}>
          {resumen.rojos > 0 && `${resumen.rojos} cosa(s) que atender ahora`}
          {resumen.rojos > 0 && resumen.amarillos > 0 && ' · '}
          {resumen.amarillos > 0 && `${resumen.amarillos} para vigilar`}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------- frescura
export function Frescura({ leidoEn, rancio, error, ahora }) {
  if (rancio) {
    const desde = error?.cuando ?? leidoEn;
    return (
      <div role="alert" className="flex items-start gap-2 rounded-xl border
                                   border-amber-300 bg-amber-50 p-3 text-amber-900">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="text-sm leading-relaxed">
          <strong>Estos números no son de ahora.</strong> Dejaron de
          actualizarse hace {Math.round((ahora - desde) / 1000)} segundos
          {error ? ` porque ${error.mensaje.toLowerCase()}` : ''}. Lo que ves
          es lo último que se supo — <strong>no decidas con esto</strong>.
          Se vuelve a intentar solo; si no se arregla, avisa a quien lleva los
          sistemas.
        </div>
      </div>
    );
  }
  return (
    <p className="text-xs text-gray-500">
      Actualizado <TiempoRelativo valor={leidoEn ? new Date(leidoEn).toISOString() : null} />.
      Se refresca solo cada {REFRESCO_MS / 1000} segundos.
    </p>
  );
}

// --------------------------------------------------------------- cuadros
export function Cuadro({ c, tv, rancio, onIr }) {
  const color = COLOR[rancio ? 'gris' : c.estado] || COLOR.gris;
  const Etiqueta = onIr ? 'button' : 'div';
  return (
    <Etiqueta
      {...(onIr ? { type: 'button', onClick: onIr } : {})}
      className={`text-left rounded-2xl border-2 p-4 ${color.caja} ${color.texto}
                  ${onIr ? 'hover:brightness-95 transition' : ''}`}
      {...(onIr ? { title: `Abrir ${c.titulo}` } : {})}
    >
      <div className={`font-bold leading-none ${tv ? 'text-7xl' : 'text-4xl'}`}>
        {rancio ? '—' : c.valor}
      </div>
      <div className={`font-semibold mt-2 ${tv ? 'text-2xl' : 'text-sm'}`}>
        {c.titulo}
      </div>
      {/* QUÉ HACER, no solo cuánto. Un tablero que solo pone números
          entrena a la gente a ignorarlos. */}
      {c.mensaje && !rancio && (
        <div className={`mt-1 opacity-80 leading-snug ${tv ? 'text-lg' : 'text-xs'}`}>
          {c.mensaje}
        </div>
      )}
    </Etiqueta>
  );
}

// --------------------------------------------------------------- eventos
function Eventos({ eventos, tv }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className={`font-semibold text-gray-800 ${tv ? 'text-2xl' : 'text-sm'}`}>
        Lo que acaba de pasar en el piso
      </div>
      <ul className="mt-2 divide-y divide-gray-100">
        {eventos.map((e) => (
          <li key={e.id} className={`py-2 flex items-center gap-2 flex-wrap
                                     ${tv ? 'text-xl' : 'text-sm'}`}>
            <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${
              e.atendida ? 'bg-gray-300' : 'bg-red-500'}`} aria-hidden="true"
            />
            <span className="font-medium text-gray-900">{e.titulo}</span>
            <span className="text-gray-600">
              en {e.ubicacion || 'un sitio sin asignar'}
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500"><TiempoRelativo valor={e.ocurrio_en} /></span>
            {/* El punto de color solo se entiende con su palabra al lado. */}
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
              e.atendida ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-800'}`}
            >
              {e.atendida ? 'Ya atendido' : 'Sin atender'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --------------------------------------------------------------- ajustes
function ModalAjustes({ onCerrar, onGuardado }) {
  const [reglas, setReglas] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const { exito } = useAvisos();

  useEffect(() => {
    api.get('/andon/reglas').then(setReglas)
      .catch((e) => setError(e instanceof ErrorApi ? e.message
        : 'No se pudieron leer los ajustes.'));
  }, []);

  function cambiar(clave, campo, valor) {
    setReglas((reglas || []).map((r) => (r.clave === clave ? { ...r, [campo]: valor } : r)));
  }

  // VALIDACIÓN EN VIVO, no al guardar. Con el rojo por debajo del amarillo
  // el tablero saltaría de verde a rojo sin pasar por el aviso, y nada
  // parecería roto.
  const malas = (reglas || []).filter(
    (r) => r.amarillo_desde != null && r.rojo_desde != null
        && Number(r.rojo_desde) < Number(r.amarillo_desde),
  );

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await api.put('/andon/reglas', (reglas || []).map((r) => ({
        clave: r.clave,
        amarillo_desde: r.amarillo_desde === '' ? null : r.amarillo_desde,
        rojo_desde: r.rojo_desde === '' ? null : r.rojo_desde,
        visible: r.visible,
        orden: r.orden,
      })));
      exito('Se guardaron los colores del tablero.');
      onGuardado();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo guardar. Vuelve a intentarlo.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      titulo="Ajustar los colores del tablero"
      subtitulo="A partir de qué número deja de estar verde cada cosa."
      icono={SlidersHorizontal}
      ancho="max-w-2xl"
      onCerrar={onCerrar}
      bloqueado={guardando}
      pie={(
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500">
            {malas.length > 0
              ? 'Corrige los avisos en rojo para poder guardar.'
              : 'Los que dejes vacíos nunca cambian de color.'}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onCerrar} disabled={guardando}
                    className="px-3 py-2 rounded-lg text-sm font-semibold border
                               border-gray-300 bg-white text-gray-700 hover:bg-gray-50
                               disabled:opacity-50">
              Cancelar
            </button>
            <button type="button" onClick={guardar}
                    disabled={guardando || !reglas || malas.length > 0}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                               font-semibold bg-[var(--color-marca)] text-white
                               hover:opacity-90 disabled:opacity-50">
              {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Guardar
            </button>
          </div>
        </div>
      )}
    >
      <div className="p-4 space-y-3 text-sm">
        {error && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-800">
            {error}
          </div>
        )}
        <p className="text-gray-600 leading-relaxed">
          Cada cuadro del tablero se pone <strong className="text-amber-700">amarillo</strong>{' '}
          cuando llega al primer número y <strong className="text-red-700">rojo</strong>{' '}
          cuando llega al segundo. Si tu almacén siempre tiene diez salidas
          esperando permiso, sube el número: un tablero rojo todo el día no
          avisa de nada.
        </p>

        {reglas === null ? (
          <div className="flex items-center gap-2 text-gray-500 py-6 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Leyendo los ajustes…
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded-xl">
            {(reglas || []).map((r) => {
              const mala = malas.some((m) => m.clave === r.clave);
              return (
                <li key={r.clave} className="p-3 space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 min-w-0 flex-1">
                      <input
                        type="checkbox"
                        checked={r.visible}
                        onChange={(e) => cambiar(r.clave, 'visible', e.target.checked)}
                      />
                      <span className="font-medium text-gray-900 truncate">{r.titulo}</span>
                    </label>
                    <label className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-amber-700">Amarillo desde</span>
                      <input
                        type="number" min="0" inputMode="numeric"
                        className="entrada w-20"
                        aria-label={`Amarillo desde, ${r.titulo}`}
                        value={r.amarillo_desde ?? ''}
                        disabled={!r.visible}
                        onChange={(e) => cambiar(r.clave, 'amarillo_desde',
                          e.target.value === '' ? null : Number(e.target.value))}
                      />
                    </label>
                    <label className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-red-700">Rojo desde</span>
                      <input
                        type="number" min="0" inputMode="numeric"
                        className="entrada w-20"
                        aria-label={`Rojo desde, ${r.titulo}`}
                        value={r.rojo_desde ?? ''}
                        disabled={!r.visible}
                        onChange={(e) => cambiar(r.clave, 'rojo_desde',
                          e.target.value === '' ? null : Number(e.target.value))}
                      />
                    </label>
                  </div>
                  {mala && (
                    <p role="alert" className="text-xs text-red-700">
                      El rojo tiene que ser igual o mayor que el amarillo. Si no,
                      nunca verías el amarillo: el cuadro saltaría de verde a rojo.
                    </p>
                  )}
                  {!r.visible && (
                    <p className="text-xs text-gray-500">
                      No saldrá en el tablero. Vuelve a marcarlo para verlo otra vez.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
